import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasPermission, Permission } from '../types';
import { fetchMyAcl } from '../services/api';
import { useSiteContext, isCommandCenterView } from './SiteContext';
import { ROUTES } from '../constants';

interface PermissionsContextValue {
  /** Site-scoped permissions. undefined = loading, null = no perms / error. */
  userPermissions: string[] | null | undefined;
  /** Org-level permissions. */
  orgPermissions: string[];
  /** True when the org has the publishing feature enabled (org-level flag). */
  orgPublishingEnabled: boolean;
  /**
   * Effective org upload limits (backend-resolved override or default).
   * undefined while the ACL is still loading — callers should fall back to
   * the static default constants until then.
   */
  orgZipSizeLimitMb: number | undefined;
  orgPdfSizeLimitMb: number | undefined;
  orgMaxDitaFilesPerZip: number | undefined;
  /** True when the user can view/generate the access control signing secret. */
  canDownloadSigningKey: boolean;
  /** True when the user can view and edit the access control configuration. */
  canManageAccessControl: boolean;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

export function usePermissionsContext(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissionsContext must be used within a PermissionsProvider');
  return ctx;
}

interface PermissionsProviderProps {
  isAuthenticated: boolean;
  userOrgIds: string[];
  children: React.ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({
  isAuthenticated,
  userOrgIds,
  children,
}) => {
  const { selectedSite, activeView } = useSiteContext();
  const navigate = useNavigate();

  const [userPermissions, setUserPermissions] = useState<string[] | null | undefined>(undefined);
  const [orgPermissions, setOrgPermissions] = useState<string[]>([]);
  const [orgPublishingEnabled, setOrgPublishingEnabled] = useState(false);
  const [orgZipSizeLimitMb, setOrgZipSizeLimitMb] = useState<number | undefined>(undefined);
  const [orgPdfSizeLimitMb, setOrgPdfSizeLimitMb] = useState<number | undefined>(undefined);
  const [orgMaxDitaFilesPerZip, setOrgMaxDitaFilesPerZip] = useState<number | undefined>(undefined);
  const [orgPermissionsLoaded, setOrgPermissionsLoaded] = useState(false);

  // Site-scoped permissions (and the org-level publishing flag, which must
  // reflect the org that owns the *selected site* — not necessarily the user's
  // first org — so it is read from this same site-scoped ACL response).
  useEffect(() => {
    if (!selectedSite) {
      setUserPermissions(undefined);
      setOrgPublishingEnabled(false);
      setOrgZipSizeLimitMb(undefined);
      setOrgPdfSizeLimitMb(undefined);
      setOrgMaxDitaFilesPerZip(undefined);
      return;
    }

    let cancelled = false;
    setUserPermissions(undefined); // loading

    fetchMyAcl(selectedSite.org_id, selectedSite.site_id)
      .then((data) => {
        if (cancelled) return;
        setUserPermissions(data?.effective_permissions ?? null);
        setOrgPublishingEnabled(data?.publishing_enabled ?? false);
        setOrgZipSizeLimitMb(data?.zip_size_limit_mb);
        setOrgPdfSizeLimitMb(data?.pdf_size_limit_mb);
        setOrgMaxDitaFilesPerZip(data?.max_dita_files_per_zip);
      })
      .catch(() => {
        if (cancelled) return;
        setUserPermissions(null);
        setOrgPublishingEnabled(false);
        setOrgZipSizeLimitMb(undefined);
        setOrgPdfSizeLimitMb(undefined);
        setOrgMaxDitaFilesPerZip(undefined);
      });

    return () => { cancelled = true; };
  }, [selectedSite]);

  // Org-level permissions (global-role gating, e.g. Command Center GLOBAL_ADMIN).
  useEffect(() => {
    if (!isAuthenticated || userOrgIds.length === 0) {
      setOrgPermissions([]);
      setOrgPermissionsLoaded(true);
      return;
    }
    setOrgPermissionsLoaded(false);
    const orgId = userOrgIds[0];
    fetchMyAcl(orgId)
      .then((data) => {
        setOrgPermissions(data?.org_permissions ?? []);
        setOrgPermissionsLoaded(true);
      })
      .catch(() => {
        setOrgPermissions([]);
        setOrgPermissionsLoaded(true);
      });
  }, [isAuthenticated, userOrgIds]);

  // Permission guard: redirect if user navigates to a gated view
  useEffect(() => {
    if (!activeView) return;

    // Command Center permission guard
    if (isCommandCenterView(activeView)) {
      // Wait until org permissions have been fetched
      if (!orgPermissionsLoaded) return;
      
      const hasGlobalAdmin = hasPermission(orgPermissions, Permission.GLOBAL_ADMIN);
      if (!hasGlobalAdmin) {
        navigate(ROUTES.HOME, { replace: true });
      }
      return;
    }
    // Publishing requires the org-level publishing flag AND the site-scoped
    // MANAGE_CONTENT permission (mirrors the backend's publish-endpoint gate).
    // Orchestrator domains have no indexed content of their own, so publishing
    // never applies to them (matches the AppSidebar nav gate).
    if (activeView === 'publishing') {
      if (!selectedSite || userPermissions === undefined) return;
      const isOrchestrator = (selectedSite.domain_type ?? 'standard') === 'orchestrator';
      const canAccessPublishing =
        !isOrchestrator &&
        orgPublishingEnabled &&
        hasPermission(userPermissions, Permission.MANAGE_CONTENT);
      if (!canAccessPublishing) {
        navigate(`/domains/${selectedSite.site_id}`, { replace: true });
      }
      return;
    }

    // Site-scoped permission guard
    if (!selectedSite || userPermissions === undefined) return;

    const hp = (p: string) => hasPermission(userPermissions, p);
    const gated =
      (activeView === 'edit' && !hp(Permission.UPDATE_SITE)) ||
      (activeView === 'addContent' && !hp(Permission.MANAGE_CONTENT)) ||
      (activeView === 'manageUsers' && !hp(Permission.MANAGE_USER)) ||
      (activeView === 'analytics' && !hp(Permission.UPDATE_SITE)) ||
      (activeView === 'tools' && !hp(Permission.UPDATE_SITE)) ||
      (activeView === 'settings' && !hp(Permission.UPDATE_SITE));

    if (gated) {
      navigate(`/domains/${selectedSite.site_id}`, { replace: true });
    }
  }, [activeView, userPermissions, orgPermissions, orgPublishingEnabled, orgPermissionsLoaded, selectedSite, navigate]);

  const value = useMemo<PermissionsContextValue>(() => ({
    userPermissions,
    orgPermissions,
    orgPublishingEnabled,
    orgZipSizeLimitMb,
    orgPdfSizeLimitMb,
    orgMaxDitaFilesPerZip,
    canDownloadSigningKey: hasPermission(userPermissions, Permission.DOWNLOAD_SIGNING_KEY),
    canManageAccessControl: hasPermission(userPermissions, Permission.MANAGE_ACCESS_CONTROL),
  }), [userPermissions, orgPermissions, orgPublishingEnabled, orgZipSizeLimitMb, orgPdfSizeLimitMb, orgMaxDitaFilesPerZip]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};
